/**
 * Facebook / Meta Pixel — per-customer, dashboard-controlled conversion tracking.
 *
 * The widget mounts directly into the host page DOM (see embed.tsx), so it shares
 * the page `window` and can both bootstrap the pixel and fire events on it.
 *
 * Design notes:
 * - Injected ONLY when the customer's config enables it (`fbPixelEnabled` +
 *   `fbPixelId`). When disabled, this module has ZERO effect — no script tag, no
 *   globals touched — so every channel stays byte-equivalent to before.
 * - Uses `trackSingle` (not `track`) so a host page's OWN pixel is never affected
 *   by our PageView / Lead events, and vice-versa.
 * - Idempotent: re-initialising the same pixel id is a no-op; a host page that
 *   already loaded the base `fbevents.js` loader is reused rather than re-injected.
 * - Fire-and-forget, fully guarded — never throws into the widget runtime.
 *
 * Module-level state is just two nullable references and function declarations
 * (no side effects at import time), so this file is safe to import from the
 * shared shell as well as the voice and chat panels.
 */

type FbqQueued = { callMethod?: (...args: unknown[]) => void; queue: unknown[][] };
type FbqWindow = Window & {
  fbq?: ((...args: unknown[]) => void) & Partial<FbqQueued>;
  _fbq?: unknown;
};

// The pixel id we have already initialised + PageView'd, or null if none.
let initializedPixelId: string | null = null;

/** Inject Meta's base loader if it isn't present yet. Returns false when the
 *  DOM/window isn't available (SSR safety). Does NOT init any pixel or fire
 *  PageView — that is done per-pixel in initFacebookPixel via trackSingle. */
function ensureBaseLoader(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  const w = window as FbqWindow;
  if (w.fbq) return true;

  // Standard Meta Pixel bootstrap (queues calls until fbevents.js loads).
  const n = function (...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
  } as ((...args: unknown[]) => void) & FbqQueued & { push?: unknown; loaded?: boolean; version?: string };
  w.fbq = n;
  if (!w._fbq) w._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];

  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const s = document.getElementsByTagName('script')[0];
  s?.parentNode?.insertBefore(t, s);
  return true;
}

/**
 * Initialise the customer's pixel and fire a scoped PageView. No-op unless the
 * customer enabled it. Safe to call repeatedly (e.g. on every config update).
 */
export function initFacebookPixel(pixelId?: string | null, enabled?: boolean): void {
  if (!enabled || !pixelId) return;
  if (initializedPixelId === pixelId) return; // already set up this pixel
  if (!ensureBaseLoader()) return;
  try {
    const { fbq } = window as FbqWindow;
    if (!fbq) return;
    fbq('init', pixelId);
    fbq('trackSingle', pixelId, 'PageView');
    initializedPixelId = pixelId;
  } catch {
    // Silent — tracking must never break the widget.
  }
}

/**
 * Fire a `Lead` conversion on the customer's pixel. Scoped with trackSingle so a
 * host page's own pixel is untouched. No-op when the pixel was never initialised
 * (i.e. the customer has it disabled).
 */
export function trackFacebookLead(): void {
  if (!initializedPixelId) return;
  try {
    const { fbq } = window as FbqWindow;
    fbq?.('trackSingle', initializedPixelId, 'Lead');
  } catch {
    // Silent.
  }
}
